import Icon from '@material-ui/core/Icon';
import { useState, useEffect } from 'react';
import { apiClient } from '@services/api';

const IOS_URL     = process.env.REACT_APP_IOS_DOWNLOAD_URL     || '#';
const ANDROID_URL = process.env.REACT_APP_ANDROID_DOWNLOAD_URL || '#';

export default function AppDownloadBanner({ customerEmail, customerId }) {
  const [copied, setCopied]       = useState(false);
  const [appStatus, setAppStatus] = useState(null); // null = carregando

  useEffect(() => {
    if (!customerId) return;
    apiClient.httpInstance
      .get(`/customers/${customerId}/app-status`)
      .then(r => setAppStatus(r.data))
      .catch(() => setAppStatus({ installed: false, last_seen: null, platform: null }));
  }, [customerId]);

  const handleCopy = () => {
    const text = customerEmail
      ? `Olá! Baixe o FT App para acompanhar suas reservas e passeios com a Full Tour:\n\niOS: ${IOS_URL}\nAndroid: ${ANDROID_URL}`
      : `Baixe o FT App:\n\niOS: ${IOS_URL}\nAndroid: ${ANDROID_URL}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const platformLabel = (p) => {
    if (!p) return null;
    if (p.toLowerCase().includes('ios') || p.toLowerCase().includes('apple')) return { label: 'iOS', icon: 'phone_iphone' };
    if (p.toLowerCase().includes('android')) return { label: 'Android', icon: 'phone_android' };
    return { label: p, icon: 'smartphone' };
  };

  const installed = appStatus?.installed;
  const lastSeen  = appStatus?.last_seen;
  const platform  = platformLabel(appStatus?.platform);

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #0f2340 0%, #1a3a5c 100%)',
        borderRadius: 10,
        padding: '20px 24px',
        border: '1px solid #234d7a',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 20 }}>
        {/* Ícone + texto */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1, minWidth: 220 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14,
            background: process.env.REACT_APP_PRIMARY_COLOR || '#62DEC2',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Icon style={{ color: '#0f2340', fontSize: 28 }}>phone_iphone</Icon>
          </div>
          <div>
            <div style={{ color: '#fff', fontWeight: 600, fontSize: 15, fontFamily: 'Poppins' }}>
              FT App — Full Tour
            </div>
            <div style={{ color: '#a0b8cc', fontSize: 12, fontFamily: 'Poppins', marginTop: 2 }}>
              Disponível para iOS e Android
            </div>
          </div>
        </div>

        {/* Status do app */}
        {appStatus === null && customerId ? (
          <div style={{ color: '#4a6a84', fontSize: 12, fontFamily: 'Poppins' }}>Verificando...</div>
        ) : appStatus !== null ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            {/* Badge instalado/não instalado */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', borderRadius: 20,
              background: installed ? '#0d2e1a' : '#1a1a2e',
              border: `1px solid ${installed ? '#22c55e' : '#4a4a6a'}`,
            }}>
              <div style={{
                width: 8, height: 8, borderRadius: '50%',
                background: installed ? '#22c55e' : '#6b6b8a',
              }} />
              <span style={{ color: installed ? '#bbf7d0' : '#8ba5c4', fontSize: 12, fontFamily: 'Poppins', fontWeight: 600 }}>
                {installed ? 'App instalado' : 'Não instalado'}
              </span>
              {installed && platform && (
                <Icon style={{ fontSize: 14, color: '#bbf7d0', marginLeft: 2 }}>{platform.icon}</Icon>
              )}
            </div>

            {/* Última abertura */}
            {installed && lastSeen && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon style={{ fontSize: 14, color: '#4a6a84' }}>access_time</Icon>
                <span style={{ color: '#8ba5c4', fontSize: 12, fontFamily: 'Poppins' }}>
                  Última abertura: <strong style={{ color: '#a0b8cc' }}>{formatDate(lastSeen)}</strong>
                </span>
              </div>
            )}
          </div>
        ) : null}

        {/* Botões de download */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <StoreButton icon="apple"   label="App Store"   href={IOS_URL} />
          <StoreButton icon="android" label="Google Play" href={ANDROID_URL} />
          <button
            onClick={handleCopy}
            title="Copiar mensagem com links para enviar ao cliente"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 8,
              border: '1px solid #234d7a',
              background: copied ? '#1a4a2e' : 'transparent',
              color: copied ? '#4caf50' : '#a0b8cc',
              cursor: 'pointer', fontSize: 13, fontFamily: 'Poppins',
              transition: 'all 0.2s', whiteSpace: 'nowrap',
            }}
          >
            <Icon style={{ fontSize: 16 }}>{copied ? 'check' : 'content_copy'}</Icon>
            {copied ? 'Copiado!' : 'Copiar link'}
          </button>
        </div>
      </div>
    </div>
  );
}

function StoreButton({ icon, label, href }) {
  const disabled = href === '#';
  const primaryColor = process.env.REACT_APP_PRIMARY_COLOR || '#62DEC2';
  return (
    <a
      href={disabled ? undefined : href}
      target={disabled ? undefined : '_blank'}
      rel="noreferrer"
      onClick={disabled ? (e) => e.preventDefault() : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: 7,
        padding: '8px 14px', borderRadius: 8,
        border: `1px solid ${disabled ? '#234d7a' : primaryColor}`,
        background: disabled ? 'transparent' : primaryColor + '18',
        color: disabled ? '#4a6a84' : primaryColor,
        textDecoration: 'none', fontSize: 13,
        fontFamily: 'Poppins', fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1, whiteSpace: 'nowrap',
      }}
    >
      <Icon style={{ fontSize: 17 }}>{icon === 'apple' ? 'phone_iphone' : 'phone_android'}</Icon>
      {label}
    </a>
  );
}
